import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Alert } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth.api';
import { extractApiError } from '../api/error';

/** Self-service change password — available to every logged-in user. */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => { setCurrent(''); setNew(''); setConfirm(''); setError(null); setDone(false); };
  const close = () => { reset(); onClose(); };

  const mut = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => { setDone(true); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  const submit = () => {
    setError(null);
    if (newPassword.length < 8) return setError('New password must be at least 8 characters');
    if (newPassword !== confirm) return setError('Passwords do not match');
    mut.mutate();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>Password changed successfully.</Alert>
        ) : (
          <Stack spacing={1.5} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Current Password" type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} fullWidth autoFocus />
            <TextField label="New Password" type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} fullWidth helperText="Minimum 8 characters" />
            <TextField label="Confirm New Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} fullWidth />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && <Button variant="contained" disabled={!currentPassword || !newPassword || mut.isPending} onClick={submit}>Change Password</Button>}
      </DialogActions>
    </Dialog>
  );
}
